// Build intro: steps carousel + optional scheme reveal
import { getEl } from '../../core/dom.js';

let bound = false;
let revealBound = false;
let gestureStartIndex = 0;
let programmaticScroll = false;
let scrollEndTimer = 0;

function getCards(track: HTMLElement): NodeListOf<Element> {
  return track.querySelectorAll('.build-step-card');
}

function getTrackSnapInset(track: HTMLElement): number {
  const style = getComputedStyle(track);
  const pad = parseFloat(style.scrollPaddingLeft);
  if (Number.isFinite(pad) && pad > 0) return pad;
  const fallback = parseFloat(style.paddingLeft);
  return Number.isFinite(fallback) ? fallback : 10;
}

function getCardScrollLeft(track: HTMLElement, index: number): number {
  const card = getCards(track)[index];
  if (!(card instanceof HTMLElement)) return 0;
  return Math.max(0, card.offsetLeft - getTrackSnapInset(track));
}

function getActiveStepIndex(track: HTMLElement): number {
  const cards = getCards(track);
  if (!cards.length) return 0;

  const trackRect = track.getBoundingClientRect();
  const anchorX = trackRect.left + getTrackSnapInset(track);
  let active = 0;
  let bestDist = Infinity;

  cards.forEach((card, i) => {
    const rect = card.getBoundingClientRect();
    const dist = Math.abs(rect.left - anchorX);
    if (dist < bestDist) {
      bestDist = dist;
      active = i;
    }
  });

  return active;
}

function clampToGestureRange(index: number, maxIndex: number): number {
  const minIdx = Math.max(0, gestureStartIndex - 1);
  const maxIdx = Math.min(maxIndex, gestureStartIndex + 1);
  return Math.min(maxIdx, Math.max(minIdx, index));
}

function syncBuildStepsCarousel(): void {
  const track = getEl('build-steps-track');
  if (!track) return;

  const active = getActiveStepIndex(track);
  const carousel = track.closest('.build-steps-carousel');
  if (carousel instanceof HTMLElement) {
    const cards = getCards(track);
    const lastIdx = cards.length - 1;
    const atEnd =
      lastIdx < 1 ||
      active >= lastIdx ||
      track.scrollLeft >= getCardScrollLeft(track, lastIdx) - 2;
    carousel.classList.toggle('is-at-end', atEnd);
  }
}

function scrollBuildStepTo(index: number, smooth = true): void {
  const track = getEl('build-steps-track');
  if (!track) return;
  const cards = getCards(track);
  const card = cards[index];
  if (!(card instanceof HTMLElement)) return;

  programmaticScroll = true;
  gestureStartIndex = index;
  track.scrollTo({
    left: getCardScrollLeft(track, index),
    behavior: smooth ? 'smooth' : 'auto'
  });
  syncBuildStepsCarousel();

  window.setTimeout(() => {
    programmaticScroll = false;
  }, smooth ? 420 : 80);
}

function settleCarousel(): void {
  const track = getEl('build-steps-track');
  if (!track || programmaticScroll) return;

  const cards = getCards(track);
  const maxIndex = cards.length - 1;
  const active = getActiveStepIndex(track);
  const target = clampToGestureRange(active, maxIndex);
  const targetLeft = getCardScrollLeft(track, target);

  if (target !== active || Math.abs(track.scrollLeft - targetLeft) > 3) {
    scrollBuildStepTo(target, true);
    return;
  }

  gestureStartIndex = target;
  syncBuildStepsCarousel();
}

function scheduleScrollSettle(): void {
  window.clearTimeout(scrollEndTimer);
  scrollEndTimer = window.setTimeout(() => settleCarousel(), 120);
}

function toggleBuildStepsScheme(): void {
  const view = document.getElementById('view-build');
  const btn = getEl('build-steps-reveal');
  if (!view) return;

  const isOpen = view.classList.toggle('build-steps-open');
  if (btn instanceof HTMLButtonElement) {
    btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }

  if (isOpen) {
    syncBuildStepsCarousel();
  }
}

function resetBuildStepsScheme(): void {
  const view = document.getElementById('view-build');
  const btn = getEl('build-steps-reveal');
  if (!view) return;

  view.classList.remove('build-steps-open');
  if (btn instanceof HTMLButtonElement) {
    btn.setAttribute('aria-expanded', 'false');
  }
}

function replayBuildIntroAnimations(): void {
  const view = document.getElementById('view-build');
  if (!view) return;
  view.classList.remove('build-intro-animate');
  void view.offsetWidth;
  view.classList.add('build-intro-animate');
}

function initBuildStepsReveal(): void {
  if (revealBound) return;
  const btn = getEl('build-steps-reveal');
  if (!(btn instanceof HTMLButtonElement)) return;
  revealBound = true;

  btn.addEventListener('click', () => {
    toggleBuildStepsScheme();
  });
}

export function initBuildIntroCarousel(): void {
  if (bound) return;
  const track = getEl('build-steps-track');
  if (!track) return;
  bound = true;

  gestureStartIndex = getActiveStepIndex(track);

  track.addEventListener(
    'pointerdown',
    () => {
      gestureStartIndex = getActiveStepIndex(track);
    },
    { passive: true }
  );

  let scrollRaf = 0;
  track.addEventListener(
    'scroll',
    () => {
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(() => {
        scrollRaf = 0;
        syncBuildStepsCarousel();
      });
      scheduleScrollSettle();
    },
    { passive: true }
  );

  track.addEventListener(
    'scrollend',
    () => {
      settleCarousel();
    },
    { passive: true }
  );

  syncBuildStepsCarousel();
}

export function refreshBuildIntroCarousel(): void {
  const track = getEl('build-steps-track');
  const scroller = document.querySelector('#view-build .page-content--build-intro');

  resetBuildStepsScheme();

  if (scroller instanceof HTMLElement) scroller.scrollTop = 0;
  if (track instanceof HTMLElement) {
    track.scrollLeft = 0;
    gestureStartIndex = 0;
  }

  replayBuildIntroAnimations();
  syncBuildStepsCarousel();
}

initBuildIntroCarousel();
initBuildStepsReveal();
